// Payload types + adapters for the Personalized Plan endpoints.
//   Endpoint A (SAT): tutors/digital/sat/personalizedPlan/<studentId>/
//   Endpoint A (ACT): tutors/digital/act/personalizedPlan/<studentId>/
//   Endpoint B:       tutors/students/<studentId>/studyTracking/?testType=...
//
// SAT and ACT payloads are normalized client-side into ONE generic PlanData so
// the components are driven by `meta` (sections, leverageMode) rather than being
// hardcoded to Reading & Writing / Math. The SAT backend payload is unchanged —
// adaptSat() maps it; the ACT endpoint already carries a `meta` block.

export type TestKind = 'sat' | 'act' | 'shsat' | 'tsia2';
export type LeverageMode = 'scaled-points' | 'accuracy-gap';

export interface SectionConfig {
    key: string;      // 'rw'|'math' (SAT) or 'english'|'math'|'reading'|'science' (ACT)
    label: string;
    color: string;
    ceiling: number;  // 800 (SAT section) or 36 (ACT section)
}

// TSIA2 only: one bar per (section, stage) in Performance History, so the
// CRC and the Diagnostic are shown separately.
export interface StageConfig {
    key: string;      // 'elar_crc' | 'elar_diagnostic' | 'math_crc' | 'math_diagnostic'
    section: string;  // section key the stage belongs to
    stage: string;    // 'crc' | 'diagnostic'
    label: string;    // 'ELAR CRC', 'Math Diagnostic', ...
    color: string;
}

export interface PlanMeta {
    testKind: TestKind;
    leverageMode: LeverageMode;
    sections: SectionConfig[];
    compositeKind: 'sum' | 'average';   // SAT total = sum; ACT composite = average
    compositeLabel: string;             // 'Total' | 'Composite'
    compositeCeiling: number;           // 1600 | 36
    compositeKeys: string[];            // section keys that feed the composite (ACT excludes Science)
    hasStem: boolean;
    // SHSAT only: 'percent' → show the level as raw "X / N" but plot trends and
    // deltas as percentage (the 100-q and 114-q forms aren't comparable raw).
    trendUnit?: 'value' | 'percent';
    stages?: StageConfig[];             // TSIA2: per-stage bars in Performance History
}

export interface PlanTest {
    studentTestId: string;
    name: string;
    date: string;                                 // YYYY-MM-DD
    sectionScores: Record<string, number | null>; // keyed by section key
    composite: number | null;                     // SAT total, ACT composite, SHSAT raw total
    stem: number | null;                          // ACT only
    sectionMax?: Record<string, number | null>;   // SHSAT: per-section question count (50/57)
    compositeMax?: number | null;                 // SHSAT: total question count (100/114)
    stageScores?: Record<string, number | null>;  // TSIA2: raw correct per stage (null = not served)
    stageMax?: Record<string, number | null>;     // TSIA2: questions on that stage
}

export interface PlanDomain {
    domain: string;
    section: string;
    color: string;
    values: (number | null)[];   // per-test accuracy 0..1, aligned to tests[]
}

export interface PlanSkill {
    skill: string;
    domain: string;
    section: string;
    color: string;
    pct: number;        // lifetime accuracy 0..100
    qPerTest: number;
    attempted: number;
    correct: number;
    values: (number | null)[];
    ptUpsideMax?: number;        // scaled-points leverage (SAT + ACT)
    ptUpsideExpected?: number;   // scaled-points leverage (SAT + ACT)
    qMissedPerTest?: number;     // accuracy-gap leverage (SHSAT)
}

export interface PlanSnapshot {
    composite: number | null;    // SAT currentTotal; ACT currentComposite; SHSAT raw total
    compositeMax: number | null; // SHSAT: latest test's total question count (100/114)
    stem: number | null;         // ACT only
    perSection: Record<string, { score: number | null; date: string | null; max?: number | null }>;
    leverageTotal: number;       // SAT/ACT Σ top upside (ACT is composite-scale, ÷3); SHSAT Σ top questions-missed
}

export interface DomainRollup {
    domain: string;
    section: string;
    color: string;
    q: number;
    acc: number;
    pts?: number;      // scaled-points (SAT + ACT) — points on the table
    ptsMax?: number;   // scaled-points (SAT + ACT) — full bar
    qMissed?: number;  // accuracy-gap (SHSAT) — questions missed per test
}

export interface PlanData {
    student: { name: string };
    meta: PlanMeta;
    snapshot: PlanSnapshot | null;   // null = no qualifying tests of this kind
    tests: PlanTest[];
    domains: PlanDomain[];
    skills: PlanSkill[];
    top: PlanSkill[];
    domainRollup: DomainRollup[];
}

const SAT_SECTIONS: SectionConfig[] = [
    { key: 'rw', label: 'Reading & Writing', color: '#0575a2', ceiling: 800 },
    { key: 'math', label: 'Math', color: '#a23205', ceiling: 800 },
];

// Normalize the (unchanged) SAT endpoint payload into the generic model.
export function adaptSat(raw: any): PlanData {
    const meta: PlanMeta = {
        testKind: 'sat', leverageMode: 'scaled-points', sections: SAT_SECTIONS,
        compositeKind: 'sum', compositeLabel: 'Total', compositeCeiling: 1600,
        compositeKeys: ['rw', 'math'], hasStem: false,
    };
    const tests: PlanTest[] = (raw.tests || []).map((t: any) => ({
        studentTestId: t.studentTestId, name: t.name, date: t.date,
        sectionScores: { rw: t.scoreRW, math: t.scoreM },
        composite: t.total, stem: null,
    }));
    const snapshot: PlanSnapshot | null = raw.snapshot ? {
        composite: raw.snapshot.currentTotal, compositeMax: null, stem: null,
        perSection: {
            rw: { score: raw.snapshot.currentRW, date: raw.snapshot.currentRWDate },
            math: { score: raw.snapshot.currentMath, date: raw.snapshot.currentMathDate },
        },
        leverageTotal: raw.snapshot.totalTopUpside,
    } : null;
    return {
        student: raw.student, meta, snapshot, tests,
        domains: raw.domains || [], skills: raw.skills || [],
        top: raw.top || [], domainRollup: raw.domainRollup || [],
    };
}

// Normalize the ACT endpoint payload (carries its own `meta`) into the model.
export function adaptAct(raw: any): PlanData {
    const m = raw.meta || {};
    const meta: PlanMeta = {
        testKind: 'act', leverageMode: m.leverageMode || 'accuracy-gap',
        sections: m.sections || [],
        compositeKind: 'average', compositeLabel: 'Composite',
        compositeCeiling: m.ceiling || 36,
        compositeKeys: m.compositeKeys || ['english', 'math', 'reading'], hasStem: true,
    };
    const tests: PlanTest[] = (raw.tests || []).map((t: any) => ({
        studentTestId: t.studentTestId, name: t.name, date: t.date,
        sectionScores: t.sectionScores || {}, composite: t.composite, stem: t.stem,
    }));
    const snapshot: PlanSnapshot | null = raw.snapshot ? {
        composite: raw.snapshot.currentComposite, compositeMax: null, stem: raw.snapshot.currentSTEM,
        perSection: raw.snapshot.perSection || {},
        leverageTotal: raw.snapshot.totalTopUpside,
    } : null;
    return {
        student: raw.student, meta, snapshot, tests,
        domains: raw.domains || [], skills: raw.skills || [],
        top: raw.top || [], domainRollup: raw.domainRollup || [],
    };
}

// SHSAT (English Language Arts + Mathematics) — 2-level INTERNAL taxonomy like
// SAT, but raw-correct scoring + accuracy-gap leverage like ACT. The backend
// sends a full meta block; these are only fallbacks if it's missing.
const SHSAT_SECTIONS: SectionConfig[] = [
    { key: 'ela', label: 'ELA', color: '#0575a2', ceiling: 50 },
    { key: 'math', label: 'Math', color: '#a23205', ceiling: 50 },
];

// Normalize the SHSAT endpoint payload (carries its own `meta`) into the model.
export function adaptShsat(raw: any): PlanData {
    const m = raw.meta || {};
    const meta: PlanMeta = {
        testKind: 'shsat', leverageMode: m.leverageMode || 'accuracy-gap',
        sections: m.sections || SHSAT_SECTIONS,
        compositeKind: m.compositeKind || 'sum',
        compositeLabel: m.compositeLabel || 'Total',
        compositeCeiling: m.compositeCeiling || 100,
        compositeKeys: m.compositeKeys || ['ela', 'math'], hasStem: false,
        trendUnit: m.trendUnit || 'percent',
    };
    const tests: PlanTest[] = (raw.tests || []).map((t: any) => ({
        studentTestId: t.studentTestId, name: t.name, date: t.date,
        sectionScores: t.sectionScores || {}, composite: t.composite, stem: null,
        sectionMax: t.sectionMax || {}, compositeMax: t.compositeMax ?? null,
    }));
    const snapshot: PlanSnapshot | null = raw.snapshot ? {
        composite: raw.snapshot.currentComposite, compositeMax: raw.snapshot.currentCompositeMax ?? null, stem: null,
        perSection: raw.snapshot.perSection || {},
        leverageTotal: raw.snapshot.totalTopQMissed,
    } : null;
    return {
        student: raw.student, meta, snapshot, tests,
        domains: raw.domains || [], skills: raw.skills || [],
        top: raw.top || [], domainRollup: raw.domainRollup || [],
    };
}

// TSIA2 (ELAR + Mathematics) — SHSAT-shaped: 2-level taxonomy, raw "X / N"
// level with percent trends (N is the number of questions served, which
// depends on whether the student was routed into a Diagnostic stage), and
// accuracy-gap leverage. The backend sends a full meta block; these are only
// fallbacks if it's missing.
const TSIA2_SECTIONS: SectionConfig[] = [
    { key: 'elar', label: 'ELAR', color: '#0575a2', ceiling: 78 },
    { key: 'math', label: 'Math', color: '#a23205', ceiling: 68 },
];

// Normalize the TSIA2 endpoint payload (carries its own `meta`) into the model.
export function adaptTsia2(raw: any): PlanData {
    const m = raw.meta || {};
    const meta: PlanMeta = {
        testKind: 'tsia2', leverageMode: m.leverageMode || 'accuracy-gap',
        sections: m.sections || TSIA2_SECTIONS,
        compositeKind: m.compositeKind || 'sum',
        compositeLabel: m.compositeLabel || 'Total',
        compositeCeiling: m.compositeCeiling || 146,
        compositeKeys: m.compositeKeys || ['elar', 'math'], hasStem: false,
        trendUnit: m.trendUnit || 'percent',
        stages: m.stages || [],
    };
    const tests: PlanTest[] = (raw.tests || []).map((t: any) => ({
        studentTestId: t.studentTestId, name: t.name, date: t.date,
        sectionScores: t.sectionScores || {}, composite: t.composite, stem: null,
        sectionMax: t.sectionMax || {}, compositeMax: t.compositeMax ?? null,
        stageScores: t.stageScores || {}, stageMax: t.stageMax || {},
    }));
    const snapshot: PlanSnapshot | null = raw.snapshot ? {
        composite: raw.snapshot.currentComposite, compositeMax: raw.snapshot.currentCompositeMax ?? null, stem: null,
        perSection: raw.snapshot.perSection || {},
        leverageTotal: raw.snapshot.totalTopQMissed,
    } : null;
    return {
        student: raw.student, meta, snapshot, tests,
        domains: raw.domains || [], skills: raw.skills || [],
        top: raw.top || [], domainRollup: raw.domainRollup || [],
    };
}

export const testTypeParam = (kind: TestKind): string =>
    (kind === 'act' ? 'Digital ACT' : kind === 'shsat' ? 'Digital SHSAT' : kind === 'tsia2' ? 'TSIA2' : 'Digital SAT');

// ── Study Tracking (Endpoint B) — test-type-agnostic ───────────────────────
export type ActivityStatus = 'completed' | 'in_progress' | 'not_started' | 'incomplete';

export interface StudyChannel {
    label: string;
    min: number;
    color: string;
    // Practice Tests channel only: minutes within `min` that are ESTIMATED from
    // pre-tracking test activity (per-question timeSpent) rather than logged via
    // a StudySession. > 0 → the total is marked with a "~".
    estimatedMin?: number;
}

export interface TrackingCounts {
    assigned: number;
    completed: number;
}

export interface TrackingTestRow {
    id: string;
    name: string;
    assignedDate: string;
    completedDate: string | null;
    total: number | null;
    scoreRW: number | null;
    scoreM: number | null;
    durationMin: number | null;
    durationEstimated?: boolean;   // true → durationMin is estimated from test activity (pre-tracking)
    status: ActivityStatus;
}

export interface TrackingAssignmentRow {
    id: string;
    name: string;
    sectionLabel: string | null;
    assignedDate: string;
    completedDate: string | null;
    score: number | null;       // percent
    durationMin: number | null;
    status: ActivityStatus;
}

export interface TrackingVocabRow {
    id: number;
    name: string;
    assignedDate: string;
    completedDate: string | null;
    quiz: string | null;
    quizScore: number | null;   // percent
    status: ActivityStatus;
}

export interface TrackingData {
    study: {
        total: number;
        test: StudyChannel;
        assignments: StudyChannel;
        vocab: StudyChannel;
    };
    counts: {
        tests: TrackingCounts;
        assignments: TrackingCounts;
        vocab: TrackingCounts;
    };
    testsList: TrackingTestRow[];
    assignmentsList: TrackingAssignmentRow[];
    vocabList: TrackingVocabRow[];
}

export const fmtDate = (iso: string | null): string => {
    if (!iso) return '—';
    return new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', timeZone: 'UTC',
    });
};

export const fmtDuration = (min: number | null): string => {
    if (min == null) return '—';
    if (min < 60) return `${min}m`;
    const h = Math.floor(min / 60);
    const m = min % 60;
    return m === 0 ? `${h}h` : `${h}h ${m}m`;
};

export const fmtHours = (min: number): string => `${Math.floor(min / 60)}h ${min % 60}m`;
